<template>
    <div>
        <v-row class="justify-center mt-10">
            <h2>MSRP statisztika</h2>
        </v-row>

        <v-row justify="center">
            <v-col cols="12" md="6" class="d-flex justify-center">
                <v-radio-group v-model="groupBy" row>
                    <v-radio color="red" label="Napi" value="napi"></v-radio>
                    <v-radio color="red" label="Heti" value="heti"></v-radio>
                    <v-radio color="red" label="Havi" value="havi"></v-radio>
                    <v-radio color="red" label="Évi" value="évi"></v-radio>
                </v-radio-group>
            </v-col>
        </v-row>

        <v-row class="justify-center">
            <v-data-table
                :headers="headers"
                :items="msrpGrouped"
                :items-per-page="15"
                class="elevation-1"
            >
                <template v-slot:item.performance="{ item }">
                    <v-progress-linear
                    :value="item.performance"
                    :color="getPerformanceColor(item.performance)"
                    height="15"
                    rounded
                    striped
                    >
                        <strong>{{ item.performance }}%</strong>
                    </v-progress-linear>
                </template>
            </v-data-table>
        </v-row>

        <v-spacer></v-spacer>

        <v-row class="justify-center mt-10">
            <h2>Csomagolás diagram</h2>
            <msrp-chart :msrpGrouped="msrpGrouped"/>
        </v-row>
    </div>
</template>
<script>
export default {
    props: ['dates', 'workDays', 'msrpStats', 'holidays', 'dailyMsrp'],

    data() {
        return {
            groupBy: 'heti',
            headers: [
                { text: 'Dátum', value: 'label' },
                { text: 'Munkanapok', value: 'workDays' },
                { text: 'Lecsomagolt (db)', value: 'totalPacked' },
                { text: 'MSRP (€)', value: 'totalMsrp' },
                { text: 'Elvárt (€)', value: 'expectedMsrp' },
                { text: 'Teljesítmény %', value: 'performance' },
            ],
        }
    },

    computed: {
        msrpGrouped() {
            const dataMap = {};

            // Összegzés meglévő statisztikák alapján
            for (const item of this.msrpStats) {
                const dateObj = new Date(item.date);
                let key = '';

                switch (this.groupBy) {
                    case 'napi': {
                        key = dateObj.toLocaleDateString('hu-HU')
                        break;
                    }
                    case 'heti': {
                        const [monday, sunday] = this.getStartAndEndOfWeek(dateObj);
                        key = `${monday} – ${sunday}`;
                        break;
                    }
                    case 'havi': {
                        const y = dateObj.getFullYear();
                        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
                        key = `${y}-${m}`;
                        break;
                    }
                    case 'évi': {
                        key = `${dateObj.getFullYear()}`;
                        break;
                    }
                }

                if (!dataMap[key]) {
                    dataMap[key] = { totalPacked: 0, totalMsrp: 0 };
                }

                dataMap[key].totalPacked += Number(item.packed_by_day);
                dataMap[key].totalMsrp += Number(item.msrp_by_day);
            }

            // Teljes idősor generálása és kitöltése nullákkal, ha nincs adat
            const timeKeys = this.generateTimeKeys(this.groupBy, this.dates[0], this.dates[1]);

            return timeKeys.map(({ key, label }) => {
                const [startDate, endDate] = this.getDateRangeFromKey(key);
                const workDays = this.countWorkDaysInRange(startDate, endDate);
                const totalPacked = dataMap[key]?.totalPacked || 0;
                const totalMsrp = dataMap[key]?.totalMsrp || 0;
                const expectedMsrp = workDays * this.dailyMsrp;
                const performance = expectedMsrp > 0 ? Math.round((totalMsrp / expectedMsrp) * 100) : 0;

                return {
                    label,
                    totalPacked,
                    totalMsrp,
                    workDays,
                    expectedMsrp,
                    performance,
                };
            });
        },

        holidaysSet() {
            return new Set(this.holidays.map(h => new Date(h.date).toLocaleDateString('hu-HU')));
        } 
    },

    methods: {
        getWeekNumber(date) {
            const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            const dayNum = d.getUTCDay() || 7;
            d.setUTCDate(d.getUTCDate() + 4 - dayNum);
            const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
            return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
        },

        getStartAndEndOfWeek(date) {
            const d = new Date(date);
            const day = d.getDay();
            const diffToMonday = day === 0 ? -6 : 1 - day; // Sunday: -6, Monday: 0, Tuesday: -1, etc.
            const monday = new Date(d);
            monday.setDate(d.getDate() + diffToMonday);

            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);

            const format = (dt) => dt.toLocaleDateString('hu-HU'); // yyyy-mm-dd

            return [format(monday), format(sunday)];
        },

        getDayName(dateStr) {
            const date = new Date(dateStr);
            const name = date.toLocaleDateString('hu-HU', { weekday: 'long' });
            return name.charAt(0).toUpperCase() + name.slice(1);
        },

        generateTimeKeys(groupBy, start, end) {
            const keys = [];
            const rangeStart = new Date(start + " 00:00:00");
            const rangeEnd = new Date(end + " 23:59:59");

            let current = new Date(rangeStart);

            while (current <= rangeEnd) {
                let key = '';
                let label = '';
                let next;

                switch (groupBy) {
                    case 'napi': {
                        const localeDate = current.toLocaleDateString('hu-HU');
                        key = localeDate;
                        label = `${localeDate} (${this.getDayName(localeDate)})`;

                        next = new Date(current.getFullYear(), current.getMonth(), current.getDate() + 1);
                        break;
                    }

                    case 'heti': {
                        const [monday, sunday] = this.getStartAndEndOfWeek(current);
                        key = `${monday} – ${sunday}`;
                        label = key;

                        // Következő hét hétfője
                        const daysUntilNextMonday = (8 - current.getDay()) % 7 || 7;
                        next = new Date(current.getFullYear(), current.getMonth(), current.getDate() + daysUntilNextMonday);
                        break;
                    }

                    case 'havi': {
                        const y = current.getFullYear();
                        const m = String(current.getMonth() + 1).padStart(2, '0');
                        key = `${y}-${m}`;
                        label = key;

                        next = new Date(current.getFullYear(), current.getMonth() + 1, 1);
                        break;
                    }

                    case 'évi': {
                        const y = current.getFullYear();
                        key = `${y}`;
                        label = key;

                        next = new Date(current.getFullYear() + 1, 0, 1);
                        break;
                    }
                }

                // Ne lépj túl az intervallum végén
                if (current > rangeEnd) break;

                keys.push({ key, label });
                current = next;
            }

            return keys;
        },

        getDateRangeFromKey(key) {
            switch (this.groupBy) {
                case 'napi':
                    return [new Date(key), new Date(key)];
                case 'heti': {
                    const [start, end] = key.split(' – ');
                    return [new Date(start), new Date(end)];
                }
                case 'havi': {
                    const [year, month] = key.split('-');
                    const start = new Date(year, month - 1, 1);
                    const end = new Date(year, month, 0); // hónap utolsó napja
                    return [start, end];
                }
                case 'évi': {
                    const year = parseInt(key);
                    return [new Date(year, 0, 1), new Date(year, 11, 31)];
                }
            }
        },


        countWorkDaysInRange(start, end) {
            const rangeStart = new Date(this.dates[0] + " 00:00:00");
            const rangeEnd = new Date(this.dates[1] + " 23:59:59");

            // Igazítsuk a heti intervallum határait a kiválasztott dátumintervallumhoz
            const from = new Date(Math.max(start, rangeStart));
            const to = new Date(Math.min(end, rangeEnd));

            let count = 0;
            const current = new Date(from);

            while (current <= to) {
                const day = current.getDay();
                const localeDate = current.toLocaleDateString('hu-HU');
                if (day >= 1 && day <= 5 && !this.holidaysSet.has(localeDate)) {
                    count++;
                }
                current.setDate(current.getDate() + 1);
            }

            return count;
        },

        getPerformanceColor(performance) {
            if (performance < 50) return 'red lighten-2';
            if (performance < 80) return 'orange lighten-2';
            return 'green lighten-2';
        },
    },
}
</script>